// src/components/ClientForgotPassword.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import MuiAlert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import "./styles.css";

const API_URL = process.env.REACT_APP_API_URL;

const requestClientOtpAPI = (email, redirect_url, signal) =>
  fetch(`${API_URL}/auth/client/send-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, redirect_url }),
    signal,
  });

const resetClientPasswordWithOtpAPI = ({
  email,
  otp,
  password,
  confirm_password,
  signal,
}) =>
  fetch(`${API_URL}/auth/client/reset-with-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, otp, password, confirm_password }),
    signal,
  });

const SuccessBanner = ({ children }) => (
  <Stack spacing={2} sx={{ width: "100%", marginTop: "20px" }}>
    <MuiAlert severity="success" variant="filled">
      {children}
    </MuiAlert>
  </Stack>
);

// Try to pull field-specific error from common API shapes
const extractFieldError = (resp, field) => {
  if (!resp) return "";
  if (Array.isArray(resp.errors)) {
    const found = resp.errors.find((e) => e.property === field);
    if (found?.constraints) return Object.values(found.constraints).pop() || "";
    if (Array.isArray(found?.messages)) return found.messages[0];
    if (typeof found?.message === "string") return found.message;
  }
  if (resp[field]) return String(resp[field]);
  return "";
};

// Strong password validator
const evaluatePassword = (pwd) => {
  const checks = [
    { key: "len", label: "At least 8 characters", pass: pwd.length >= 8 },
    {
      key: "upper",
      label: "One uppercase letter (A-Z)",
      pass: /[A-Z]/.test(pwd),
    },
    {
      key: "lower",
      label: "One lowercase letter (a-z)",
      pass: /[a-z]/.test(pwd),
    },
    { key: "num", label: "One number (0-9)", pass: /[0-9]/.test(pwd) },
    {
      key: "special",
      label: "One special character (!@#$%^&* etc.)",
      pass: /[^A-Za-z0-9]/.test(pwd),
    },
  ];
  const valid = checks.every((c) => c.pass);
  return { valid, checks };
};

export default function ClientForgotPassword() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const p = new URLSearchParams(search);
  const primary = p.get("primary");
  const secondary = p.get("secondary");
  const bgHex = p.get("bg");
  const bgImageRaw = p.get("bg_image");
  const redirect_url=p.get("redirect_url");
  const incomingTitle = p.get("title");
  const faviconParam = p.get("favicon");

  // const redirect_url = new URLSearchParams(search).get("redirect_url") || "";
  const signinUrl = `/signin/client?redirect_url=${encodeURIComponent(redirect_url)}&primary=${encodeURIComponent(primary || "")}&secondary=${encodeURIComponent(secondary || "")}&bg=${encodeURIComponent(bgHex || "")}&bg_image=${encodeURIComponent(bgImageRaw || "")}&title=${encodeURIComponent(incomingTitle || "")}&favicon=${encodeURIComponent(faviconParam || "")}`;

  const [step, setStep] = useState(1);
  const [showLoading, setShowLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [email, setEmail] = useState("");
  const normalizedEmail = useMemo(() => email.trim().toLowerCase(), [email]);
  const [verificationLinkSent, setVerificationLinkSent] = useState(false);
  const [emailValidationErrors, setEmailValidationErrors] = useState("");

  const [cooldown, setCooldown] = useState(0);

  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm_password, setConfirmPassword] = useState("");
  const [otpValidationErrors, setOtpValidationErrors] = useState("");
  const [passwordValidationErrors, setPasswordValidationErrors] = useState("");
  const [confirmValidationErrors, setConfirmValidationErrors] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // password rule state
  const pwdEval = useMemo(() => evaluatePassword(password), [password]);
  const isPasswordStrong = pwdEval.valid;
  const doPasswordsMatch =
    confirm_password.length > 0 && password === confirm_password;

  // redirect countdown after success
  const [redirectCountdown, setRedirectCountdown] = useState(3);
  const timersRef = useRef({ interval: null, timeout: null });

  const abortRef = useRef(null);
  const abortRunning = () => {
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch {}
    }
    abortRef.current = new AbortController();
    return abortRef.current.signal;
  };

  const clearAllErrors = () => {
    setErrorMessage("");
    setEmailValidationErrors("");
    setOtpValidationErrors("");
    setPasswordValidationErrors("");
    setConfirmValidationErrors("");
  };

  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const isEmailValid = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail),
    [normalizedEmail]
  );

  // Enable submit only when OTP present, strong pwd, and match
  const canSubmitReset =
    Boolean(otp.trim()) && isPasswordStrong && doPasswordsMatch;

  // Live inline errors for password fields
  useEffect(() => {
    if (password && !isPasswordStrong) {
      setPasswordValidationErrors("Please meet all password rules below.");
    } else {
      setPasswordValidationErrors("");
    }
  }, [password, isPasswordStrong]);

  useEffect(() => {
    if (!confirm_password) setConfirmValidationErrors("");
    else if (password !== confirm_password)
      setConfirmValidationErrors("Passwords must match");
    else setConfirmValidationErrors("");
  }, [password, confirm_password]);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    clearAllErrors();
    if (!isEmailValid) {
      setEmailValidationErrors("Please enter a valid email.");
      return;
    }

    try {
      setShowLoading(true);
      const signal = abortRunning();
      const res = await requestClientOtpAPI(
        normalizedEmail,
        redirect_url,
        signal
      );
      const responseData = await res.json().catch(() => ({}));
      setShowLoading(false);

      if (res.status === 200 || res.status === 201) {
        setVerificationLinkSent(true);
        setStep(2);
        setCooldown(60);
      } else {
        // Surface server message, including "email not registered"
        const msg =
          extractFieldError(responseData, "email") ||
          responseData.message ||
          "Failed to send OTP.";
        setEmailValidationErrors(msg);
      }
    } catch {
      setShowLoading(false);
      setErrorMessage("Something went wrong. Please try again.");
    }
  };

  const handleResend = async () => {
    if (cooldown) return;
    clearAllErrors();
    try {
      setShowLoading(true);
      const signal = abortRunning();
      const res = await requestClientOtpAPI(
        normalizedEmail,
        redirect_url,
        signal
      );
      await res.json().catch(() => ({}));
      setShowLoading(false);
      setCooldown(60);
    } catch {
      setShowLoading(false);
      setErrorMessage("Something went wrong. Please try again.");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    clearAllErrors();

    let ok = true;
    if (!otp.trim()) {
      setOtpValidationErrors("OTP is required");
      ok = false;
    }
    if (!password) {
      setPasswordValidationErrors("Password is required");
      ok = false;
    }
    if (!isPasswordStrong) {
      setPasswordValidationErrors("Please meet all password rules below.");
      ok = false;
    }
    if (!confirm_password) {
      setConfirmValidationErrors("Confirm password is required");
      ok = false;
    } else if (password !== confirm_password) {
      setConfirmValidationErrors("Passwords must match");
      ok = false;
    }
    if (!ok) return;

    try {
      setShowLoading(true);
      const signal = abortRunning();
      const res = await resetClientPasswordWithOtpAPI({
        email: normalizedEmail,
        otp,
        password,
        confirm_password,
        signal,
      });
      const responseData = await res.json().catch(() => ({}));
      setShowLoading(false);
      if (res.status === 200 || res.status === 201) {
        setResetSuccess(true);
        setRedirectCountdown(3);

        // start countdown + navigate to signin
        timersRef.current.interval = setInterval(() => {
          setRedirectCountdown((s) => (s > 1 ? s - 1 : 1));
        }, 1000);

        timersRef.current.timeout = setTimeout(() => {
          navigate(signinUrl);
        }, 3000);
      } else {
        // Surface specific server error if present
        const msg =
          extractFieldError(responseData, "password") ||
          responseData.message ||
          "Failed to reset password.";
        setErrorMessage(msg);
      }
    } catch {
      setShowLoading(false);
      setErrorMessage("Something went wrong. Please try again.");
    }
  };

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch {}
      }
      if (timersRef.current.interval) clearInterval(timersRef.current.interval);
      if (timersRef.current.timeout) clearTimeout(timersRef.current.timeout);
    };
  }, []);

  return (
    <>
      <h2>Forgot Password</h2>

      {step === 1 && (
        <form onSubmit={handleRequestOtp} noValidate>
          <div className="inputbox relative border-b-2 border-white field-input bg-transparent mb-4">
            <input
              type="email"
              placeholder="Email ID"
              className="input w-full max-w-sm bg-transparent focus:outline-none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {!!emailValidationErrors && (
              <p className="text-[#ff3333] text-sm mt-1">
                {emailValidationErrors}
              </p>
            )}
          </div>

          {errorMessage && (
            <p className="text-[#ff3333] mb-2">{errorMessage}</p>
          )}
          {showLoading && <p className="text-white mb-2">Requesting OTP ...</p>}

          <button
            type="submit"
            disabled={showLoading || !isEmailValid}
            className="w-full rounded-full py-2 font-semibold text-white duration-300 button-background"
          >
            {showLoading ? "Submitting..." : "Send OTP"}
          </button>

          <div className="w-full flex justify-end mt-2 pb-5 pt-2">
            <Link
              className="text-blue-400 hover:underline link text-white"
              to={signinUrl}
            >
              Signin
            </Link>
          </div>

          {verificationLinkSent && (
            <SuccessBanner>
              OTP sent to <u>{normalizedEmail}</u>
            </SuccessBanner>
          )}
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleResetPassword} noValidate>
          {/* Readonly Email + Edit */}
          <div className="inputbox relative border-b-2 border-white field-input bg-transparent mb-4">
            <input
              type="email"
              placeholder="Email ID"
              className="input w-full max-w-sm bg-transparent focus:outline-none pr-10"
              value={normalizedEmail}
              readOnly
            />
            <span
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-55%)",
                cursor: "pointer",
                color: "white",
              }}
              className="hover:text-gray-300"
              onClick={() => {
                setStep(1);
                setVerificationLinkSent(false);
                setCooldown(0);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setStep(1);
                  setVerificationLinkSent(false);
                  setCooldown(0);
                }
              }}
              aria-label="Edit email"
            >
              <EditIcon fontSize="small" />
            </span>
          </div>

          {/* OTP */}
          <div className="inputbox mb-1 flex items-center gap-2">
            <input
              type="text"
              placeholder="Enter OTP"
              className="input flex-1"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
            <button
              type="button"
              onClick={handleResend}
              disabled={cooldown > 0}
              className="px-3 py-1 rounded bg-gray-700 text-white text-sm disabled:opacity-50 hover:bg-gray-600 transition-colors"
              aria-label="Resend OTP"
              title={cooldown ? `Wait ${cooldown}s` : "Resend OTP"}
            >
              {cooldown ? `Resend (${cooldown}s)` : "Resend"}
            </button>
          </div>
          <p className="text-[#ff3333] mt-1 text-sm">{otpValidationErrors}</p>

          {/* New Password */}
          <div className="inputbox relative border-b-2 border-white bg-transparent mb-2">
            <div className="relative flex items-center">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="New Password"
                className="input w-full max-w-sm bg-transparent focus:outline-none pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-white hover:text-gray-300"
                onClick={() => setShowPassword((v) => !v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    setShowPassword((v) => !v);
                }}
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </span>
            </div>
            {!!passwordValidationErrors && (
              <p className="text-[#ff3333] text-sm mt-1">
                {passwordValidationErrors}
              </p>
            )}
          </div>

          {/* Password rules checklist */}
          <ul className="text-xs leading-5 mb-3 pl-5 list-disc text-white">
            {pwdEval.checks.map((c) => (
              <li
                key={c.key}
                className={`${
                  c.pass ? "text-green-300" : "text-red-300"
                } transition-colors duration-200`}
              >
                {c.pass ? "✓" : "✗"} {c.label}
              </li>
            ))}
          </ul>

          {/* Confirm Password */}
          <div className="inputbox relative border-b-2 border-white bg-transparent mb-4">
            <div className="relative flex items-center">
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm Password"
                className="input w-full max-w-sm bg-transparent focus:outline-none pr-10"
                value={confirm_password}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <span
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-white hover:text-gray-300"
                onClick={() => setShowConfirm((v) => !v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    setShowConfirm((v) => !v);
                }}
                aria-label={
                  showConfirm
                    ? "Hide confirm password"
                    : "Show confirm password"
                }
                title={showConfirm ? "Hide" : "Show"}
              >
                {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </span>
            </div>
            {!!confirmValidationErrors && (
              <p className="text-[#ff3333] text-sm mt-1">
                {confirmValidationErrors}
              </p>
            )}
          </div>

          {/* Global errors / success */}
          {errorMessage && (
            <p className="text-[#ff3333] mb-2">{errorMessage}</p>
          )}
          {showLoading && (
            <p className="text-white mb-2">Resetting password...</p>
          )}
          {resetSuccess && (
            <SuccessBanner>
              Password reset successful. You can now sign in. Redirecting in{" "}
              {redirectCountdown}s…
            </SuccessBanner>
          )}

          <button
            type="submit"
            disabled={showLoading || !canSubmitReset}
            className="w-full rounded-full py-2 font-semibold text-white duration-300 button-background disabled:opacity-60"
          >
            {showLoading ? "Submitting..." : "Reset Password"}
          </button>

          <div className="w-full flex justify-end mt-2 pb-5 pt-2">
            <Link
              className="text-blue-400 hover:underline link text-white"
              to={signinUrl}
            >
              Signin
            </Link>
          </div>
        </form>
      )}
    </>
  );
}
